// src/components/ResetPassword.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiFetch } from '../api'

function KirassMark({ className = '' }) {
  return (
    <svg viewBox="0 0 48 48" className={className} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M10 24L24 10h10L20 24l14 14H24L10 24z" fill="currentColor" opacity="0.95" />
      <path d="M22 24L34 12h4L26 24l12 12h-4L22 24z" fill="currentColor" opacity="0.55" />
    </svg>
  )
}

function EyeIcon({ open }) {
  return open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 9a3 3 0 1 1 0 6 3 3 0 0 1 0-6Z" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 3l18 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path
        d="M2 12s3.5-7 10-7c2.2 0 4.1.7 5.7 1.7M22 12s-3.5 7-10 7c-2.2 0-4.1-.7-5.7-1.7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M10.2 10.2A3 3 0 0 0 12 15a3 3 0 0 0 1.8-.6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}

function IconMail(props) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true" {...props}>
      <path d="M4 6h16v12H4V6Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M4.5 7l7.5 6 7.5-6" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  )
}

export default function ResetPassword({
  tenantName,
  productName = 'Votre structure',
  platformBrand = 'KIRASS GROUP',
  adminEmail = 'azdinekirasse1@gmail.com',
  madeBy = 'AKGroup UB',
  signatureLabel = 'Conception & intégration',
}) {
  const navigate = useNavigate()

  // ✅ Change ici si ton backend est /api/auth/reset au lieu de /api/auth/reset-password
  const RESET_ENDPOINT = '/api/auth/reset-password'

  const [pw1, setPw1] = useState('')
  const [pw2, setPw2] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')

  const pwRef = useRef(null)

  const orgName = (tenantName || productName || 'Votre structure').trim()
  const year = new Date().getFullYear()

  const tokenFromUrl = useMemo(() => {
    try {
      const sp = new URLSearchParams(window.location.search)
      return sp.get('token') || ''
    } catch {
      return ''
    }
  }, [])

  useEffect(() => {
    setTimeout(() => pwRef.current?.focus(), 80)
  }, [])

  const adminMailto = useMemo(() => {
    const subject = `[${orgName}] Problème de réinitialisation`
    const body =
      `Bonjour,\n\n` +
      `Je n’arrive pas à réinitialiser mon mot de passe sur: ${orgName}\n` +
      `Lien/token: ${tokenFromUrl ? '(token présent)' : '(token manquant)'}\n\n` +
      `Merci.\n`
    return `mailto:${adminEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  }, [adminEmail, orgName, tokenFromUrl])

  const CardShell =
    'h-full rounded-[28px] border border-slate-200 bg-white/75 shadow-[0_25px_90px_-60px_rgba(15,23,42,.20)] backdrop-blur-xl'

  function validate(pw) {
    if (!pw || pw.length < 12) return 'Le mot de passe doit contenir au moins 12 caractères.'
    if (!/[A-Z]/.test(pw)) return 'Le mot de passe doit contenir au moins une majuscule.'
    if (!/[a-z]/.test(pw)) return 'Le mot de passe doit contenir au moins une minuscule.'
    if (!/[0-9]/.test(pw)) return 'Le mot de passe doit contenir au moins un chiffre.'
    if (!/[^A-Za-z0-9]/.test(pw)) return 'Le mot de passe doit contenir au moins un caractère spécial.'
    return ''
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (loading) return

    setError('')
    setInfo('')

    if (!tokenFromUrl) {
      setError("Lien invalide: token manquant.")
      return
    }

    const v = validate(pw1)
    if (v) {
      setError(v)
      setTimeout(() => pwRef.current?.focus(), 60)
      return
    }
    if (pw1 !== pw2) {
      setError('Les mots de passe ne correspondent pas.')
      return
    }

    setLoading(true)
    try {
      await apiFetch(RESET_ENDPOINT, {
        method: 'POST',
        body: { token: tokenFromUrl, new_password: pw1 },
      })
      setInfo('Mot de passe mis à jour. Tu peux te connecter.')
      setTimeout(() => navigate('/auth'), 700)
    } catch (err) {
      console.error('Reset error:', err)
      setError("Le lien est expiré ou invalide. Demande un nouveau lien.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen text-slate-900">
      {/* Background clair/frais */}
      <div className="fixed inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-slate-100" />
        <div className="absolute -top-40 left-[-10%] h-[520px] w-[520px] rounded-full bg-sky-300/50 blur-3xl" />
        <div className="absolute top-10 right-[-15%] h-[560px] w-[560px] rounded-full bg-slate-300/40 blur-3xl" />
        <div className="absolute bottom-[-20%] left-[35%] h-[520px] w-[520px] rounded-full bg-emerald-300/35 blur-3xl" />
        <div className="absolute inset-0 opacity-[0.18] bg-[linear-gradient(to_right,rgba(15,23,42,.09)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,.09)_1px,transparent_1px)] bg-[size:56px_56px]" />
      </div>

      <div className="relative mx-auto flex min-h-screen max-w-6xl items-stretch px-4 py-10">
        <div className="grid w-full grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-8 items-stretch">
          {/* LEFT (desktop) */}
          <div className={`relative hidden lg:flex lg:col-span-6 ${CardShell} p-8 flex-col justify-between min-h-[560px]`}>
            <div>
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm">
                  <KirassMark className="h-7 w-7 text-white" />
                </div>
                <div className="leading-tight">
                  <div className="text-[11px] tracking-[0.22em] text-slate-500">{platformBrand}</div>
                  <div className="text-lg font-semibold text-slate-900">{orgName}</div>
                </div>
              </div>

              <div className="mt-12 max-w-sm">
                <div className="text-3xl font-semibold tracking-tight text-slate-900">Nouveau mot de passe</div>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">
                  Choisis un mot de passe solide (8+). Si le lien est expiré, refais une demande.
                </p>

                <a
                  href={adminMailto}
                  className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-medium text-white shadow-[0_18px_60px_-40px_rgba(15,23,42,.35)] transition hover:bg-slate-800"
                  title="Ouvrir la messagerie (mail pré-rempli)"
                >
                  <IconMail className="opacity-90" />
                  Contacter l’admin
                </a>
              </div>
            </div>

            <div>
              <div className="mt-6 flex items-center justify-between text-xs text-slate-500">
                <span>© {year} {orgName}</span>
                <span className="tracking-[0.18em] text-slate-400">Propulsé par {platformBrand}</span>
              </div>

              <div className="pointer-events-none absolute bottom-6 left-6 flex items-center gap-2 opacity-[0.06]">
                <KirassMark className="h-5 w-5 text-slate-900" />
                <span className="text-xs tracking-[0.24em] text-slate-900">{platformBrand}</span>
              </div>
            </div>
          </div>

          {/* RIGHT */}
          <div className="flex h-full items-stretch justify-center lg:col-span-6">
            <div className="w-full max-w-lg h-full flex flex-col">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1.5 shadow-sm">
                  <KirassMark className="h-4 w-4 text-slate-900" />
                  <span className="text-[11px] tracking-[0.22em] text-slate-600">{platformBrand}</span>
                </div>
                <div className="text-xs text-slate-500">© {year}</div>
              </div>

              <div className={`${CardShell} min-h-[560px] flex flex-col overflow-hidden`}>
                <div className="px-7 pt-7">
                  <div className="text-[11px] font-semibold tracking-[0.22em] text-slate-500">{orgName}</div>
                  <div className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">Réinitialisation</div>
                  <div className="mt-1 text-sm text-slate-600">
                    {tokenFromUrl ? 'Token détecté.' : 'Token manquant (lien invalide).'}
                  </div>
                </div>

                <div className="px-7 pb-7 pt-6 flex-1">
                  {error && (
                    <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                      {error}
                    </div>
                  )}
                  {info && (
                    <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800">
                      {info}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-slate-800">Nouveau mot de passe</label>
                      <div className="mt-1.5 flex gap-2">
                        <input
                          ref={pwRef}
                          type={showPw ? 'text' : 'password'}
                          value={pw1}
                          onChange={(e) => { setPw1(e.target.value); if (error || info) { setError(''); setInfo('') } }}
                          disabled={loading}
                          placeholder="12+ cars, majuscule, chiffre, sp\u00e9cial"
                          autoComplete="new-password"
                          className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400
                                     outline-none transition focus:border-slate-300 focus:ring-4 focus:ring-slate-200/60"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPw(v => !v)}
                          disabled={loading}
                          className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-3 text-slate-700 hover:bg-slate-50 transition disabled:opacity-60"
                          title={showPw ? 'Masquer' : 'Afficher'}
                          aria-label={showPw ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                        >
                          <EyeIcon open={showPw} />
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-slate-800">Confirmer</label>
                      <div className="mt-1.5">
                        <input
                          type={showPw ? 'text' : 'password'}
                          value={pw2}
                          onChange={(e) => { setPw2(e.target.value); if (error || info) { setError(''); setInfo('') } }}
                          disabled={loading}
                          placeholder="Répéter le mot de passe"
                          autoComplete="new-password"
                          className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400
                                     outline-none transition focus:border-slate-300 focus:ring-4 focus:ring-slate-200/60"
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={loading || !tokenFromUrl}
                      className={`w-full rounded-2xl px-4 py-3 text-white font-medium transition
                        ${loading || !tokenFromUrl
                          ? 'bg-slate-300 cursor-not-allowed'
                          : 'bg-slate-900 hover:bg-slate-950 shadow-[0_18px_60px_-40px_rgba(79,70,229,.55)]'
                        }`}
                    >
                      {loading ? 'Mise à jour…' : 'Mettre à jour'}
                    </button>

                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => navigate('/auth')}
                        className="text-sm font-medium text-slate-700 hover:text-slate-900"
                      >
                        ← Retour à la connexion
                      </button>

                      <a
                        href={adminMailto}
                        className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900"
                        title="Contacter l’admin"
                      >
                        <IconMail />
                        Contacter l’admin
                      </a>
                    </div>
                  </form>
                </div>

                <div className="px-7 pb-7">
                  <div className="pt-2 space-y-2">
                    <div className="text-[11px] text-center text-slate-500 leading-relaxed">
                      Astuce: évite les mots de passe simples (12345678, azdine123…).
                    </div>
                    <div className="text-[11px] text-center text-slate-500">
                      <span>{signatureLabel}</span>{' '}
                      <span className="text-slate-300">•</span>{' '}
                      <span className="font-medium text-slate-700">{madeBy}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 lg:hidden">
                <a
                  href={adminMailto}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm font-medium text-slate-900 hover:bg-white transition"
                >
                  <IconMail />
                  Contacter l’admin
                </a>
              </div>
            </div>
          </div>

          <div className="hidden lg:block lg:col-span-12">
            <div className="mt-2 text-center text-xs text-slate-500">
              <span className="opacity-80">Propulsé par</span>{' '}
              <span className="tracking-[0.22em]">{platformBrand}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
